import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link, Navigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Save, Wrench, AlertCircle } from "lucide-react";
import { getClient, getTool, createTool, updateTool } from "../../../api/chatbotApi";
import useAuth from "../../../hooks/useAuth";
import { isPlatformAdmin, isTenantAdmin } from "../../../util/accessControl";

const EXECUTOR_TYPES = ["python", "sql", "http"];

const DEFAULT_FORM = {
  name: "",
  description: "",
  executor_type: "python",
  input_schema: "{}",
  code: "",
  metadata: "{}",
  enabled: true,
};

export default function ChatbotToolEditorPage() {
  const { clientId, toolId } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !toolId || toolId === "new";
  const navigate = useNavigate();
  const toolsPath = `/clients/${clientId}/tools`;
  const { user } = useAuth();
  const canEditTools = isPlatformAdmin(user) || isTenantAdmin(user);
  const isReadOnly = !isNew && (searchParams.get("mode") === "view" || !canEditTools);

  const [clientName, setClientName] = useState("");
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!clientId) return;
    getClient(clientId)
      .then((client) => setClientName(client?.name || "Client"))
      .catch(() => setClientName("Client"));
  }, [clientId]);

  useEffect(() => {
    if (!isNew) {
      setLoading(true);
      setError("");
      getTool(toolId)
        .then((t) => {
          setForm({
            name: t.name,
            description: t.description,
            executor_type: t.executor_type,
            input_schema: JSON.stringify(t.input_schema, null, 2),
            code: t.code || "",
            metadata: JSON.stringify(t.metadata, null, 2),
            enabled: t.enabled,
          });
        })
        .catch(() => setError("Failed to load tool configuration"))
        .finally(() => setLoading(false));
    }
  }, [toolId, isNew]);

  const handleSave = async () => {
    if (!canEditTools) return;
    setSaving(true);
    setError("");
    try {
      let input_schema = {}, metadata = {};
      try { input_schema = JSON.parse(form.input_schema); } catch { setError("Invalid JSON in Input Schema"); setSaving(false); return; }
      try { metadata = JSON.parse(form.metadata); } catch { setError("Invalid JSON in Metadata"); setSaving(false); return; }

      const payload = {
        name: form.name,
        description: form.description,
        executor_type: form.executor_type,
        input_schema,
        code: form.code || null,
        metadata,
        enabled: form.enabled,
      };

      if (isNew) {
        const created = await createTool(payload, clientId);
        navigate(`${toolsPath}/${created.id}`);
      } else {
        await updateTool(toolId, payload);
        navigate(toolsPath);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Error saving tool");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Loading tool specification...</div>;
  if (isNew && !canEditTools) return <Navigate to={toolsPath} replace />;

  return (
    <div className="space-y-6">
      {/* Navigation Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to={toolsPath} className="hover:text-slate-900 transition flex items-center gap-1">
          <ArrowLeft size={16} /> Client Tools
        </Link>
        <span>/</span>
        <span className="font-semibold text-slate-900">{isNew ? "New Tool" : form.name}</span>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 flex items-center gap-2">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Wrench size={24} className="text-[#096dea]" /> {isNew ? `${clientName || "Client"} : Create Tool` : isReadOnly ? `View Tool: ${form.name}` : `Edit Tool: ${form.name}`}
        </h1>
      </div>

      {/* Form Card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
              Tool Name *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              disabled={isReadOnly}
              placeholder="get_user_info"
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm focus:border-[#096dea] focus:outline-none"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
              Executor Type *
            </label>
            <select
              value={form.executor_type}
              onChange={(e) => setForm((f) => ({ ...f, executor_type: e.target.value }))}
              disabled={isReadOnly}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm focus:border-[#096dea] focus:outline-none"
            >
              {EXECUTOR_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
            Description *
          </label>
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            disabled={isReadOnly}
            rows={2}
            placeholder="Describe what this tool does for OpenAI function calling..."
            className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-xs focus:border-[#096dea] focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
            Input Schema (OpenAPI JSON Schema)
          </label>
          <textarea
            value={form.input_schema}
            onChange={(e) => setForm((f) => ({ ...f, input_schema: e.target.value }))}
            disabled={isReadOnly}
            rows={5}
            className="w-full rounded-xl border border-slate-200 px-3.5 py-2 font-mono text-xs focus:border-[#096dea] focus:outline-none"
            placeholder={'{\n  "type": "object",\n  "properties": {}\n}'}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
            Execution Code / Query / Endpoint Config
          </label>
          <textarea
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            disabled={isReadOnly}
            rows={6}
            className="w-full rounded-xl border border-slate-200 px-3.5 py-2 font-mono text-xs focus:border-[#096dea] focus:outline-none"
            placeholder={
              form.executor_type === "python"
                ? "def execute(arguments, context):\n    return {}"
                : form.executor_type === "sql"
                ? "SELECT * FROM users WHERE id = :id"
                : "https://api.example.com/v1/user"
            }
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
            Metadata (JSON)
          </label>
          <textarea
            value={form.metadata}
            onChange={(e) => setForm((f) => ({ ...f, metadata: e.target.value }))}
            disabled={isReadOnly}
            rows={3}
            className="w-full rounded-xl border border-slate-200 px-3.5 py-2 font-mono text-xs focus:border-[#096dea] focus:outline-none"
          />
        </div>

        <div className="flex items-center gap-3 pt-1">
          <input
            type="checkbox"
            id="tool-enabled-check"
            checked={form.enabled}
            onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
            disabled={isReadOnly}
            className="h-4 w-4 rounded border-slate-300 text-[#096dea]"
          />
          <label htmlFor="tool-enabled-check" className="text-sm font-semibold text-slate-700">
            Tool Enabled
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
          {isReadOnly ? (
            <span className="mr-auto text-sm text-slate-500">Read-only tool configuration</span>
          ) : null}
          <button
            type="button"
            onClick={() => navigate(toolsPath)}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition"
          >
            {isReadOnly ? "Back" : "Cancel"}
          </button>
          {!isReadOnly ? (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !form.name.trim() || !form.description.trim()}
              className="inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0959bb] disabled:opacity-50 transition"
            >
              <Save size={16} /> {saving ? "Saving..." : isNew ? "Create Tool" : "Save Changes"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
